<template>
  <div>
    <v-divider
      v-if="!hideTopDivider"
      thickness="1"
      opacity="25"
    />
    <div class="fullscreen-table-area">
      <v-data-table
        v-if="isSelectionNonEmpty"
        v-model:sort-by="sortingBy"
        :class="[
          upperTableHasIncreased ? 'colored' : '',
          'background-color-transition',
          'upper-table',
        ]"
        :headers="headers"
        data-testid="data-table-selected-stations"
        :items="selected"
        checkbox-color="primary"
        :show-select="true"
        selectable-key="isInUpperSelection"
        :items-per-page="-1"
        :hide-default-footer="true"
        show-expand
        must-sort
        density="compact"
        :height="selectedTableHeight"
        @update:model-value="handleTableInput($event, TableType.upper)"
      >
        <template #item.data-table-select="{ item }">
          <div class="table-icon">
            <v-checkbox
              :model-value="true"
              class="d-flex align-center"
              style="height: 30px; width: 30px"
              checked
              density="compact"
              @update:model-value="(val) => toggleStationSelection(item, val, TableType.upper)"
            />
          </div>
        </template>
        <template #header.data-table-expand>
          <station-table-header-menu />
        </template>
        <template #header.data-table-select>
          <div class="table-icon">
            <v-checkbox
              :value="selectAllCheckboxIsChecked"
              :indeterminate="selectAllCheckBoxIsIntermediate"
              data-testid="select-all"
              hide-details
              density="compact"
              class="d-flex align-center"
              style="height: 30px; width: 30px"
              @click="selectAll"
            />
          </div>
        </template>
        <template #item.count="{ value }">
          <div class="d-flex align-center">
            {{ value }}
            <v-tooltip
              :text="t('station_table.combined_stations_tooltip')"
              :open-delay="200"
              right
              :nudge-bottom="35"
              content-class="station-hint"
              data-testid="tooltip-combined-stations-selected"
            >
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  :style="value <= 1 && { visibility: 'hidden' }"
                  color="primary"
                  size="x-small"
                  data-testid="tooltip-combined-stations-selected-icon"
                >
                  mdi-help
                </v-icon>
              </template>
            </v-tooltip>
          </div>
        </template>
        <template #item.startDate="{ value }">
          {{ cleanDate(value) }}
        </template>
        <template #item.endDate="{ value }">
          {{ cleanDate(value) }}
        </template>
        <template #item.state="{ value }">
          {{ cleanState(value) }}
        </template>
        <template #item.hasSunshine="{ value }">
          {{ cleanBoolean(value) }}
        </template>
        <template #item.hasGlobalRadiation="{ value }">
          {{ cleanBoolean(value) }}
        </template>
        <template #item.data-table-expand="{ internalItem, isExpanded, toggleExpand }">
          <v-btn
            icon
            variant="text"
            :style="internalItem.raw.type !== 'COMBINED' && { visibility: 'hidden' }"
            data-testid="btn-expand-row-selected"
            @click="toggleExpand(internalItem)"
          >
            <v-icon
              data-testid="btn-expand-row-selected-icon"
              :icon="isExpanded(internalItem) ? 'mdi-chevron-down' : 'mdi-chevron-up'"
            />
          </v-btn>
        </template>
        <template #expanded-row="{ item }">
          <tr class="expanded-row">
            <td
              :colspan="2"
            />
            <td
              v-for="key in stationStore.getVisibleHeaders"
              :key="key"
              class="pa-0"
            >
              <div class="d-flex flex-column">
                <div
                  v-for="station in item.subStations"
                  :key="station.id"
                  :class="
                    'substation-row d-flex px-4 align-center' +
                      (stationIsDisabled(station) ? ' zamg-text-disabled' : '')
                  "
                >
                  {{ cleanSubstationValue(station[key]) }}
                  <v-tooltip
                    v-if="stationIsDisabled(station) && key === 'count'"
                    :text="`${station.name} (${station.id}) ${t(
                      'station_table.combined_station_not_matched'
                    )}`"
                    :open-delay="200"
                    right
                    :nudge-bottom="35"
                    content-class="station-hint"
                    data-testid="tooltip-combined-stations-selected-substations"
                  >
                    <template #activator="{ props }">
                      <v-icon
                        v-bind="props"
                        color="primary"
                        size="x-small"
                        data-testid="tooltip-combined-stations-selected-substations-icon"
                      >
                        mdi-help
                      </v-icon>
                    </template>
                  </v-tooltip>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
      <v-divider
        class="pa-1"
        thickness="1"
        opacity="25"
      />
      <div class="lower-table-container">
        <v-data-table
          v-model:sort-by="sortingBy"
          :class="[
            lowerTableHasIncreased ? 'colored' : '',
            'flex-grow-1',
            'background-color-transition',
            'lower-table'
          ]"
          data-testid="data-table-all-stations"
          :headers="hiddenHeader"
          :items="availableItems"
          :items-per-page-text="footerOptions['items-per-page-text']"
          :items-per-page-options="footerOptions['items-per-page-options']"
          :show-first-last-page="footerOptions['show-first-last-page']"
          :page-text="footerOptions['page-text']"
          :loading="isLoading"
          :loading-text="t('loading')"
          :no-data-text="t('no_data_available')"
          :items-per-page="itemsPerPageDefault"
          :page="currentPage"
          checkbox-color="primary"
          :show-select="true"
          :disable-pagination="disablePagination"
          :hide-default-footer="disablePagination"
          show-expand
          must-sort
          density="compact"
          @toggle-select-all="selectAll"
          @update:page="updateCurrentPage($event)"
          @update:model-value="handleTableInput($event, TableType.lower)"
        >
          <template #item.data-table-select="{ isSelected, item }">
            <div class="table-icon">
              <v-checkbox
                class="d-flex align-center"
                style="height: 30px; width: 30px"
                density="compact"
                :value="isSelected"
                color="secondary"
                :ripple="false"
                @update:model-value="(val) => toggleStationSelection(item, val, TableType.lower)"
              />
            </div>
          </template>
          <template
            v-if="!isSelectionNonEmpty"
            #header.data-table-expand
          >
            <station-table-header-menu />
          </template>
          <template #header.data-table-select>
            <div class="table-icon">
              <v-checkbox
                v-if="!isSelectionNonEmpty"
                :value="selectAllCheckboxIsChecked"
                :indeterminate="selectAllCheckBoxIsIntermediate"
                hide-details
                density="compact"
                class="mt-0 pa-0"
                @click="selectAll"
              />
            </div>
          </template>
          <template #item.count="{ value }">
            <div class="d-flex align-center">
              {{ value }}
              <v-tooltip
                :text="t('station_table.combined_stations_tooltip')"
                :open-delay="200"
                right
                :nudge-bottom="35"
                content-class="station-hint"
                data-testid="tooltip-combined-stations-all"
              >
                <template #activator="{ props }">
                  <v-icon
                    v-bind="props"
                    :style="value <= 1 && { visibility: 'hidden' }"
                    color="primary"
                    size="x-small"
                    data-testid="tooltip-combined-stations-all-icon"
                  >
                    mdi-help
                  </v-icon>
                </template>
              </v-tooltip>
            </div>
          </template>
          <template #item.startDate="{ value }">
            {{ cleanDate(value) }}
          </template>
          <template #item.endDate="{ value }">
            {{ cleanDate(value) }}
          </template>
          <template #item.state="{ value }">
            {{ cleanState(value) }}
          </template>
          <template #item.hasSunshine="{ value }">
            {{ cleanBoolean(value) }}
          </template>
          <template #item.hasGlobalRadiation="{ value }">
            {{ cleanBoolean(value) }}
          </template>
          <template
            #item.data-table-expand="{ internalItem, isExpanded, toggleExpand }"
          >
            <v-btn
              icon
              variant="text"
              data-testid="btn-expand-row-all"
              :style="
                internalItem.raw.type !== 'COMBINED' && { visibility: 'hidden' }
              "
              @click="toggleExpand(internalItem)"
            >
              <v-icon
                data-testid="btn-expand-row-all-icon"
                :icon="isExpanded(internalItem) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
              />
            </v-btn>
          </template>
          <template #expanded-row="{ item }">
            <tr class="expanded-row">
              <td
                :colspan="2"
              />
              <td
                v-for="key in stationStore.getVisibleHeaders"
                :key="key"
                class="pa-0"
              >
                <div class="d-flex flex-column">
                  <div
                    v-for="station in item.subStations"
                    :key="station.id"
                    :class="
                      'substation-row d-flex px-4 align-center' +
                        (stationIsDisabled(station) ? ' zamg-text-disabled' : '')
                    "
                  >
                    {{ cleanSubstationValue(station[key]) }}
                    <v-tooltip
                      v-if="stationIsDisabled(station) && key === 'count'"
                      :text="`${station.name} (${station.id}) ${t(
                        'station_table.combined_station_not_matched'
                      )}`"
                      :open-delay="200"
                      right
                      :nudge-bottom="35"
                      content-class="station-hint"
                      :data-testid="'tooltip-combined-stations-all-substations-' + station.id"
                    >
                      <template #activator="{ props }">
                        <v-icon
                          v-bind="props"
                          color="primary"
                          size="x-small"
                          :data-testid="'tooltip-combined-stations-all-substations-icon-' + station.id"
                        >
                          mdi-help
                        </v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                </div>
              </td>
            </tr>
          </template>
          <template #footer.page-text="item">
            {{
              `${item.pageStart} - ${item.pageStop} ${t('of').toString()} ${
                availableItems.length
              }`
            }}
          </template>
        </v-data-table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Filters, Station } from "@/stores/station/station-model";
import { DataTableHeader } from "vuetify";
import DatasetStore from "@/stores/dataset/dataset-module";
import StationStore from "@/stores/station/station-module";
import StationTableHeaderMenu from "@/components/station/StationTableHeaderMenu.vue";
import { localeDateString, getDisplayFrequency } from "@/util/datetime";
import { Bundesland } from '@/api/out';

type BundeslandKey = keyof typeof Bundesland;

interface FooterOptions {
  'items-per-page-options': number[];
  'items-per-page-text': string;
  'page-text': string;
  'show-first-last-page': boolean;
}

enum TableType {
  'upper',
  'lower',
}

export default defineComponent({
  name: "StationTable",
  components: {
    StationTableHeaderMenu,
  },
  props: {
    items: { type: Array as () => Station[], required: true },
    isLoading: { type: Boolean, required: true },
    disablePagination: { type: Boolean, default: false },
    greyOutUnmatchedSubstations: { type: Boolean, default: true },
    hiddenHeaderNames: { type: Array as () => string[], default: () => [] },
    hideTopDivider: { type: Boolean, default: false },
    selected: { type: Array as () => Station[], default: () => [] },
    activeFilters: { type: Object as () => Filters, default: () => ({}) },
  },
  emits: ["select"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const sortingBy = ref([{key: 'id', order: 'asc' }]);

    const lastSelectedLength = ref(0);

    const upperTableHasIncreased = ref(false);
    const lowerTableHasIncreased = ref(false);
    const animationTimerId = ref(0);

    const maxSelectedStationsVisible = 3;
    const currentPage = ref(1);
    const itemsPerPageDefault = 10;

    const footerOptions = computed<FooterOptions>(() => ({
      "items-per-page-options": [5, 10, 15, 25, 50, 100],
      "items-per-page-text": t("table.rows_per_page"),
      "page-text": `{0} - {1} ${t('of').toString()} {2}`,
      "show-first-last-page": true,
    }));

    const defaultHeaders: Array<keyof Station> = [
      "count",
      "id",
      "name",
      "startDate",
      "endDate",
      "state",
      "altitude",
      "hasSunshine",
      "hasGlobalRadiation",
    ];

    const extraHeaders: string[] = ["data-table-expand", "data-table-select"];

    const stationStore = StationStore;
    const datasetStore = DatasetStore;

    watch(
      () => props.activeFilters,
      () => {
        // reset page when filters change
        currentPage.value = 1;
      }
    );

    const emitSelected = (selected: Station[]): void => {
      emit("select", selected);
    };

    const isSelectionNonEmpty = computed(() => stationStore.stationsAreSelected);

    const selectedTableHeight = computed<number | undefined>(() => {
      const rowHeight = 49;
      return stationStore.selectedStations.length > maxSelectedStationsVisible
        ? rowHeight * (maxSelectedStationsVisible + 1)
        : undefined;
    });

    const availableItems = computed(() =>
      props.items.filter((item) => !stationStore.isStationSelected(item))
    );

    const headerNames = computed(() =>
      defaultHeaders.filter((name) => !props.hiddenHeaderNames.includes(name))
    );

    const selectAllCheckboxIsChecked = computed(
      () => stationStore.selectedStations.length !== 0
    );

    const selectAllCheckBoxIsIntermediate = computed(
      () =>
        stationStore.selectedStations.length !== 0 &&
        stationStore.selectedStations.length !== props.items.length
    );

    const hiddenHeader = computed<DataTableHeader[]>(() => {
      if (!isSelectionNonEmpty.value) {
        return headers.value;
      }
      const defaultProps: Partial<DataTableHeader> = {
        headerProps: {'class': 'zamg-text-continuous zamg-text-input hide-header'},
        align: "start",
        sortable: false,
        width: "10%",
      };
      const propsForExtra: Partial<DataTableHeader> = {
        ...defaultProps,
        sortable: false,
        width: "5%",
      };
      return extraHeaders
        .map((name) => ({
          ...propsForExtra,
          title: "",
          key: name,
        }))
        .concat(
          headerNames.value
            .filter((name) => stationStore.getVisibleHeaders.includes(name))
            .map((name) => ({
              ...defaultProps,
              title: new Array(
                t(`station_table.table.${name}`).toString().length
              ).join("A"),
              key: name,
            }))
        );
    });

    const headers = computed<DataTableHeader[]>(() => {
      const defaultProps: Partial<DataTableHeader> = {
        headerProps: {'class': 'zamg-text-continuous zamg-text-input'},
        align: "start",
        sortable: true,
        width: "10%",
      };
      const propsForExtra: Partial<DataTableHeader> = {
        align: "start",
        sortable: false,
        width: "5%",
      };
      return extraHeaders
        .map((name) => ({
          ...propsForExtra,
          title: "",
          key: name,
        }))
        .concat(
          headerNames.value
            .filter((name) => stationStore.getVisibleHeaders.includes(name))
            .map((name) => ({
              ...defaultProps,
              title: t(`station_table.table.${name}`).toString(),
              key: name,
            }))
        );
    });

    const stationIsDisabled = (station: Station): boolean => {
      return (
        station.matchesFilter === false && props.greyOutUnmatchedSubstations
      );
    };

    const selectAll = (): void => {
      if (stationStore.selectedStations.length > 0) {
        emitSelected([]);
      } else {
        emitSelected(stationStore.getAvailableStations);
      }
    };

    const updateCurrentPage = (newPage: number): void => {
      currentPage.value = newPage;
    };

    const handleTableInput = (selectedStations: Station[], table: TableType) => {
      // Only emit event if stations have changed. Without that, every event is
      // triggered twice, because the tables update each other
      const lastSelected = lastSelectedLength.value;
      lastSelectedLength.value = selectedStations.length;
      if (lastSelected === selectedStations.length) {
        return;
      }
      const otherTable =
        table === TableType.lower ? TableType.upper : TableType.lower;
      animateTableHasIncreased(otherTable);
      emitSelected(selectedStations);
    };

    function toggleStationSelection(station: Station, selected: boolean, table: TableType) {
      const currentSelection = [...stationStore.selectedStations];

      if (selected) {
        // add station if not already selected
        if (!currentSelection.some(s => s.id === station.id)) {
          currentSelection.push(station);
        }
      } else {
        // remove station
        const index = currentSelection.findIndex(s => s.id === station.id);
        if (index !== -1) currentSelection.splice(index, 1);
      }

      handleTableInput(currentSelection, table);
    }

    const animateTableHasIncreased = (table: TableType): void => {
      // wait 1ms to make sure the rows have time to move to other table
      setTimeout(() => {
        setTableHasIncreased(true, table);
      }, 1);
      // set id for timer to make sure the animation keeps on going between multiple clicks
      const randomId = Math.round(Math.random() * 1000000);
      animationTimerId.value = randomId;
      // end animation after time
      setTimeout(() => {
        if (animationTimerId.value === randomId) {
          setTableHasIncreased(false, table);
        }
      }, 500);
    };

    const setTableHasIncreased = (newVal: boolean, table: TableType): void => {
      if (table === TableType.lower) {
        lowerTableHasIncreased.value = newVal;
      }
      if (table === TableType.upper) {
        upperTableHasIncreased.value = newVal;
      }
    };

    const cleanDate = (date: Date): string => {
      if (datasetStore.metadata) {
        return localeDateString(
          date,
          locale.value,
          getDisplayFrequency(datasetStore.metadata.frequency)
        );
      }
      return "";
    };

    const cleanBoolean = (value: boolean): string => {
      return value ? t("true").toString() : t("false").toString();
    };

    const cleanState = (state: string): string => {
      const key = (Object.keys(Bundesland) as Array<BundeslandKey>)
        .find(k => Bundesland[k] === state);

      return key ? t(`state.${String(key)}`) : state;
    };

    const cleanSubstationValue = (value: string | boolean | Date): string => {
      if (value instanceof Date) {
        return cleanDate(value);
      }
      if (typeof value === "boolean") {
        return cleanBoolean(value);
      }
      return value;
    };

    return {
      t,
      sortingBy,
      upperTableHasIncreased,
      lowerTableHasIncreased,
      currentPage,
      footerOptions,
      isSelectionNonEmpty,
      selectedTableHeight,
      availableItems,
      headerNames,
      selectAllCheckboxIsChecked,
      selectAllCheckBoxIsIntermediate,
      hiddenHeader,
      headers,
      stationIsDisabled,
      selectAll,
      updateCurrentPage,
      handleTableInput,
      cleanDate,
      cleanState,
      cleanBoolean,
      cleanSubstationValue,
      TableType,
      stationStore,
      toggleStationSelection,
      itemsPerPageDefault,
    };
  },
});
</script>

<style scoped>
.fullscreen-table-area {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.lower-table-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.lower-table {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

:deep(.lower-table .v-table__wrapper) {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

:deep(.lower-table > .v-data-table-footer) {
  position: relative;
  bottom: 0;
  background-color: white;
  z-index: 7;
  border-top: 1px solid rgb(var(--v-theme-divider));
  flex-shrink: 0;
}

:deep(.lower-table .v-table__wrapper thead th) {
  position: sticky;
  top: 0;
  z-index: 8;
  background-color: white;
}

:deep(.upper-table .v-table__wrapper) {
  overflow-y: auto;
  scrollbar-gutter: stable;
}

:deep(.upper-table .v-table__wrapper thead th) {
  position: sticky;
  top: 0;
  z-index: 8;
  background-color: white;
}

:deep(.v-data-table-footer) {
  /*put pagination of table to the left*/
  justify-content: flex-start !important;
}

:deep(.v-data-table-footer) {
  /*add border between table content and table footer*/
  border-top: 1px solid rgb(var(--v-theme-divider));
}

:deep(.v-data-table__tr) > td {
  /*Switch default border from bottom to top, needed to hide border when expanding a row*/
  border-bottom: 0 !important;
  border-top: 1px solid rgb(var(--v-theme-divider));
}

.expanded-row > td {
  /*Hide border between substations and next main station when expanding a row*/
  border-bottom: 0 !important;
}

:deep(.v-table__wrapper) {
  /*invert wrapper of upper table in x-direction to move scroll bar to the left*/
  direction: rtl;
}

:deep(.v-table__wrapper) > table {
  /*invert upper table (without scrollbar) in x-direction again to make sure the content is oriented in the right way */
  direction: ltr;
}

:deep(.v-table--fixed-header) > .v-table__wrapper {
  /* always show scrollbar to make sure that upper and lower table are aligned*/
  overflow-y: scroll;
}

.background-color-transition :deep(tr) {
  /*set transition for all rows in tables*/
  transition: background-color 0.25s ease !important;
}

.colored :deep(tr) {
  /*set color for all rows in tables after they have been updated*/
  background-color: rgb(var(--v-theme-primary-lighten3)) !important;
}

:deep(.hide-header) {
  height: 1px !important;
  line-height: 1px !important;
  color: transparent;
}

.table-icon {
  width: 48px;
  display: flex;
  justify-content: center;
}

.substation-row {
  height: 2rem;
  font-size: 0.75rem;
}
</style>
